/**
 * Who is signed in, and how to sign in (PLAN.md decisions 3.5 and 4.3).
 *
 * The API says which methods are on (`/client-config`). In production that is Identity Platform:
 * an emailed sign-in link, an optional password (decision 3.5a), and Google once its provider is
 * enabled. Locally the API can offer a
 * development sign-in instead, which never touches Firebase. Either way the rest of the app sees
 * one thing: a signed-in email, and an ID token attached to every API call.
 */

import type { FirebaseApp } from "firebase/app";
import type { Auth } from "firebase/auth";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { api, setTokenProvider, type Schemas } from "@/api/client";

type Method = "email_link" | "password" | "google" | "dev";

type AuthState =
  | { status: "loading" }
  | { status: "unavailable"; reason: string }
  | { status: "signed-out"; methods: Method[] }
  | { status: "signed-in"; email: string; methods: Method[] };

type AuthApi = {
  state: AuthState;
  sendSignInLink: (email: string) => Promise<void>;
  finishSignInLink: (url: string, email: string | null) => Promise<"signed-in" | "need-email" | "not-a-link">;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  sendPasswordLink: (email: string) => Promise<void>;
  finishPasswordLink: (code: string, password: string) => Promise<void>;
  setPassword: (password: string) => Promise<void>;
  signInForDevelopment: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthApi | null>(null);

const EMAIL_KEY = "sign-in-email"; // remembered between asking for the link and following it
const DEV_KEY = "dev-sign-in";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const [firebase, setFirebase] = useState<{ app: FirebaseApp; auth: Auth } | null>(null);
  const [methods, setMethods] = useState<Method[]>([]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe = () => {};
    (async () => {
      const { data, response } = await api.GET("/api/v1/client-config");
      if (cancelled) return;
      if (!data) {
        setState({ status: "unavailable", reason: `sign-in is not configured (${response.status})` });
        return;
      }
      const offered = data.sign_in_methods as Method[];
      setMethods(offered);

      if (offered.includes("dev")) {
        const email = safeGet(DEV_KEY);
        setTokenProvider(email ? async () => `dev:${email}` : null);
        setState(email ? { status: "signed-in", email, methods: offered } : { status: "signed-out", methods: offered });
        return;
      }
      const fb = await startFirebase(data.firebase);
      if (cancelled) return;
      setFirebase(fb);
      const { onAuthStateChanged } = await import("firebase/auth");
      unsubscribe = onAuthStateChanged(fb.auth, (user) => {
        if (user?.email) {
          setTokenProvider(() => user.getIdToken());
          setState({ status: "signed-in", email: user.email, methods: offered });
        } else {
          setTokenProvider(null);
          setState({ status: "signed-out", methods: offered });
        }
      });
    })().catch((err: unknown) => {
      if (!cancelled) setState({ status: "unavailable", reason: err instanceof Error ? err.message : String(err) });
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // The API emails the link itself, from the product's own domain: Firebase's own emails came from a
  // firebaseapp.com address (filed as spam) and pointed at a page that rejected them.
  const sendSignInLink = useCallback(async (email: string) => {
    const { error, response } = await api.POST("/api/v1/auth/sign-in-link", { body: { email } });
    if (!response.ok) throw new Error(detailOf(error, "the sign-in email could not be sent - try again"));
    safeSet(EMAIL_KEY, email);
  }, []);

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      if (!firebase) throw new Error("sign-in is not ready");
      const { signInWithEmailAndPassword } = await import("firebase/auth");
      try {
        await signInWithEmailAndPassword(firebase.auth, email, password);
      } catch (err) {
        throw new Error(passwordError(err));
      }
    },
    [firebase],
  );

  // Password links come from the API too: our domain, our page (/login/reset). The answer is the same
  // whether or not the address has an account.
  const sendPasswordLink = useCallback(async (email: string) => {
    const { error, response } = await api.POST("/api/v1/auth/password-link", { body: { email } });
    if (!response.ok) throw new Error(detailOf(error, "the email could not be sent - try again"));
  }, []);

  const finishPasswordLink = useCallback(
    async (code: string, password: string) => {
      const { data, error } = await api.POST("/api/v1/auth/password-reset", { body: { code, password } });
      if (!data) throw new Error(detailOf(error, "the password could not be set - try again"));
      await signInWithPassword(data.email, password);
    },
    [signInWithPassword],
  );

  // Setting a password ends the account's other sessions (see app/accounts.py), this one included,
  // so sign straight back in with it.
  const setPassword = useCallback(
    async (password: string) => {
      if (state.status !== "signed-in") throw new Error("sign in first");
      const { error, response } = await api.POST("/api/v1/me/password", { body: { password } });
      if (!response.ok) throw new Error(detailOf(error, "the password could not be set - try again"));
      await signInWithPassword(state.email, password);
    },
    [state, signInWithPassword],
  );

  const finishSignInLink = useCallback(
    async (url: string, email: string | null) => {
      if (!firebase) throw new Error("sign-in is not ready");
      const { isSignInWithEmailLink, signInWithEmailLink } = await import("firebase/auth");
      if (!isSignInWithEmailLink(firebase.auth, url)) return "not-a-link" as const;
      // Opened on another device, the remembered email is missing: ask for it rather than guess.
      const address = email ?? safeGet(EMAIL_KEY);
      if (!address) return "need-email" as const;
      await signInWithEmailLink(firebase.auth, address, url);
      safeRemove(EMAIL_KEY);
      return "signed-in" as const;
    },
    [firebase],
  );

  const signInWithGoogle = useCallback(async () => {
    if (!firebase) throw new Error("sign-in is not ready");
    const { GoogleAuthProvider, signInWithPopup } = await import("firebase/auth");
    try {
      await signInWithPopup(firebase.auth, new GoogleAuthProvider());
    } catch (err) {
      const code = (err as { code?: unknown } | null)?.code;
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") return;
      if (code === "auth/popup-blocked") throw new Error("the browser blocked the Google window - allow pop-ups and try again");
      throw new Error("Google sign-in didn't complete - try again, or use an email link");
    }
  }, [firebase]);

  const signInForDevelopment = useCallback(
    async (email: string) => {
      safeSet(DEV_KEY, email);
      setTokenProvider(async () => `dev:${email}`);
      setState({ status: "signed-in", email, methods });
    },
    [methods],
  );

  const signOut = useCallback(async () => {
    safeRemove(DEV_KEY);
    setTokenProvider(null);
    if (firebase) {
      const { signOut: firebaseSignOut } = await import("firebase/auth");
      await firebaseSignOut(firebase.auth);
    }
    setState({ status: "signed-out", methods });
  }, [firebase, methods]);

  const value = useMemo(
    () => ({
      state,
      sendSignInLink,
      finishSignInLink,
      signInWithGoogle,
      signInWithPassword,
      sendPasswordLink,
      finishPasswordLink,
      setPassword,
      signInForDevelopment,
      signOut,
    }),
    [
      state,
      sendSignInLink,
      finishSignInLink,
      signInWithGoogle,
      signInWithPassword,
      sendPasswordLink,
      finishPasswordLink,
      setPassword,
      signInForDevelopment,
      signOut,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthProvider>");
  return value;
}

async function startFirebase(config: Schemas["FirebaseConfig"]) {
  const [{ initializeApp }, { getAuth }] = await Promise.all([import("firebase/app"), import("firebase/auth")]);
  const app = initializeApp({
    apiKey: config.api_key,
    authDomain: config.auth_domain,
    projectId: config.project_id,
    appId: config.app_id,
  });
  return { app, auth: getAuth(app) };
}

function detailOf(error: unknown, fallback: string): string {
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  return typeof detail === "string" ? detail : fallback;
}

/** Firebase's codes, in words. Wrong email and wrong password read the same, so neither leaks. */
export function passwordError(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  if (code === "auth/too-many-requests") return "too many attempts - wait a while, or use an email link";
  if (code === "auth/network-request-failed") return "no connection - check your network and try again";
  if (typeof code === "string" && code.startsWith("auth/")) return "that email and password don't match";
  return err instanceof Error ? err.message : "sign-in failed";
}

// Storage can be unavailable (private windows, blocked site data): sign-in must still work.
function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* the link still works; opening it will just ask for the email again */
  }
}
function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to clean up */
  }
}
